import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Building2, Plus, Users, Edit, Trash2, Package, Crown, AlertCircle, Clock, RefreshCw, Power, FileDown } from 'lucide-react';
import { Switch } from '../components/ui/switch';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Organizations = () => {
  const { isSuperadmin } = useAuth();
  const { language } = useLanguage();
  const [organizations, setOrganizations] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Dialog state
  const [showOrgDialog, setShowOrgDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgForm, setOrgForm] = useState({
    name: '',
    subdomain: '',
    plan_id: '',
    // Admin fields for new organization
    admin_name: '',
    admin_email: '',
    admin_password: '',
  });
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [seedingPlans, setSeedingPlans] = useState(false);
  const [togglingOrg, setTogglingOrg] = useState(null);

  // Plan dialog state
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    display_name: '',
    max_users: 10,
    price_monthly: 0,
    stripe_price_id: '',
    price_per_user: '',
    stripe_price_id_per_user: '',
    features: '',
    is_active: true,
  });
  const [savingPlan, setSavingPlan] = useState(false);
  const [exportingBilling, setExportingBilling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [orgsRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/organizations`),
        axios.get(`${API_URL}/plans`)
      ]);
      setOrganizations(orgsRes.data);
      setPlans(plansRes.data);
    } catch (err) {
      setError(language === 'es' ? 'Error al cargar datos' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrg = () => {
    setEditingOrg(null);
    setOrgForm({ 
      name: '', 
      subdomain: '', 
      plan_id: plans[1]?.id || '',
      admin_name: '',
      admin_email: '',
      admin_password: '',
    });
    setShowOrgDialog(true);
  };

  const handleEditOrg = (org) => {
    setEditingOrg(org);
    setOrgForm({
      name: org.name,
      subdomain: org.subdomain || '',
      plan_id: org.plan_id,
      admin_name: '',
      admin_email: '',
      admin_password: '',
    });
    setShowOrgDialog(true);
  };

  const handleSaveOrg = async () => {
    try {
      setCreatingOrg(true);
      if (editingOrg) {
        // Update existing org (don't send admin fields)
        await axios.put(`${API_URL}/organizations/${editingOrg.id}`, {
          name: orgForm.name,
          subdomain: orgForm.subdomain,
          plan_id: orgForm.plan_id,
        });
      } else {
        // Create new org with optional admin
        const payload = {
          name: orgForm.name,
          subdomain: orgForm.subdomain,
          plan_id: orgForm.plan_id,
        };
        
        // Include admin data if provided
        if (orgForm.admin_email && orgForm.admin_password) {
          payload.admin = {
            name: orgForm.admin_name || orgForm.admin_email.split('@')[0],
            email: orgForm.admin_email,
            password: orgForm.admin_password,
          };
        }
        
        await axios.post(`${API_URL}/organizations`, payload);
      }
      setShowOrgDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving organization');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleDeleteOrg = async (org) => {
    if (!window.confirm(
      language === 'es' 
        ? `¿Estás seguro de eliminar "${org.name}"? Esta acción eliminará todos los datos asociados.`
        : `Are you sure you want to delete "${org.name}"? This will delete all associated data.`
    )) return;

    try {
      await axios.delete(`${API_URL}/organizations/${org.id}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error deleting organization');
    }
  };

  const handleSeedPlans = async () => {
    try {
      setSeedingPlans(true);
      const response = await axios.post(`${API_URL}/plans/seed`);
      const { created, already_existed } = response.data;
      
      let message = language === 'es' 
        ? 'Planes inicializados correctamente.' 
        : 'Plans initialized successfully.';
      
      if (created.length > 0) {
        message += ` ${language === 'es' ? 'Creados' : 'Created'}: ${created.join(', ')}.`;
      }
      if (already_existed.length > 0) {
        message += ` ${language === 'es' ? 'Ya existían' : 'Already existed'}: ${already_existed.join(', ')}.`;
      }
      
      alert(message);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error seeding plans');
    } finally {
      setSeedingPlans(false);
    }
  };

  const handleCreatePlan = () => {
    setEditingPlan(null);
    setPlanForm({
      name: '',
      display_name: '',
      max_users: 10,
      price_monthly: 0,
      stripe_price_id: '',
      price_per_user: '',
      stripe_price_id_per_user: '',
      features: '',
      is_active: true,
    });
    setShowPlanDialog(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      display_name: plan.display_name,
      max_users: plan.max_users,
      price_monthly: plan.price_monthly,
      stripe_price_id: plan.stripe_price_id || '',
      price_per_user: plan.price_per_user ?? '',
      stripe_price_id_per_user: plan.stripe_price_id_per_user || '',
      features: (plan.features || []).join(', '),
      is_active: plan.is_active !== false,
    });
    setShowPlanDialog(true);
  };

  const handleSavePlan = async () => {
    try {
      setSavingPlan(true);
      const featuresList = planForm.features
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);

      if (editingPlan) {
        await axios.put(`${API_URL}/plans/${editingPlan.id}`, {
          display_name: planForm.display_name,
          max_users: Number(planForm.max_users),
          price_monthly: Number(planForm.price_monthly),
          stripe_price_id: planForm.stripe_price_id || null,
          price_per_user: planForm.price_per_user === '' ? null : Number(planForm.price_per_user),
          stripe_price_id_per_user: planForm.stripe_price_id_per_user || null,
          features: featuresList,
          is_active: planForm.is_active,
        });
      } else {
        await axios.post(`${API_URL}/plans`, {
          name: planForm.name,
          display_name: planForm.display_name,
          max_users: Number(planForm.max_users),
          price_monthly: Number(planForm.price_monthly),
          stripe_price_id: planForm.stripe_price_id || null,
          price_per_user: planForm.price_per_user === '' ? null : Number(planForm.price_per_user),
          stripe_price_id_per_user: planForm.stripe_price_id_per_user || null,
          features: featuresList,
        });
      }
      setShowPlanDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!window.confirm(
      language === 'es'
        ? `¿Eliminar el plan "${plan.display_name}"? Solo se puede eliminar si ninguna organización lo usa.`
        : `Delete plan "${plan.display_name}"? Only possible if no organization is using it.`
    )) return;

    try {
      await axios.delete(`${API_URL}/plans/${plan.id}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error deleting plan');
    }
  };

  const handleExportBilling = async () => {
    try {
      setExportingBilling(true);
      const response = await axios.get(`${API_URL}/billing/admin/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'facturacion.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error exporting billing data');
    } finally {
      setExportingBilling(false);
    }
  };

  const BILLING_STATUS_LABELS = {
    es: { trialing: 'En prueba', active: 'Activa', past_due: 'Pago pendiente', canceled: 'Cancelada' },
    en: { trialing: 'Trial', active: 'Active', past_due: 'Past due', canceled: 'Canceled' },
  };
  const BILLING_STATUS_COLORS = {
    trialing: 'bg-blue-100 text-blue-700',
    active: 'bg-emerald-100 text-emerald-700',
    past_due: 'bg-amber-100 text-amber-700',
    canceled: 'bg-red-100 text-red-700',
  };

  const handleToggleOrgStatus = async (org) => {
    try {
      setTogglingOrg(org.id);
      await axios.put(`${API_URL}/organizations/${org.id}/toggle-status`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error toggling organization status');
    } finally {
      setTogglingOrg(null);
    }
  };

  const getPlanBadgeColor = (planName) => {
    switch (planName) {
      case 'basic': return 'bg-slate-100 text-slate-700';
      case 'professional': return 'bg-indigo-100 text-indigo-700';
      case 'enterprise': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (!isSuperadmin()) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {language === 'es' ? 'Acceso Restringido' : 'Access Restricted'}
            </h3>
            <p className="text-slate-500 text-center">
              {language === 'es' 
                ? 'Solo los superadministradores pueden acceder a esta sección.'
                : 'Only superadmins can access this section.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Crown className="w-7 h-7 text-amber-500" />
            {language === 'es' ? 'Gestión de Organizaciones' : 'Organization Management'}
          </h1>
          <p className="text-slate-500 mt-1">
            {language === 'es' 
              ? 'Administra las empresas y sus planes de suscripción'
              : 'Manage companies and their subscription plans'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleSeedPlans} 
            variant="outline"
            disabled={seedingPlans}
            title={language === 'es' ? 'Inicializar planes de suscripción por defecto' : 'Initialize default subscription plans'}
            data-testid="seed-plans-btn"
          >
            {seedingPlans ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Package className="w-4 h-4 mr-2" />
            )}
            {language === 'es' ? 'Seed Planes' : 'Seed Plans'}
          </Button>
          <Button onClick={handleCreatePlan} variant="outline" data-testid="new-plan-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Nuevo Plan' : 'New Plan'}
          </Button>
          <Button onClick={handleExportBilling} variant="outline" disabled={exportingBilling} data-testid="export-billing-btn">
            {exportingBilling ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4 mr-2" />
            )}
            {language === 'es' ? 'Exportar Facturación' : 'Export Billing'}
          </Button>
          <Button onClick={handleCreateOrg} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-org-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Nueva Organización' : 'New Organization'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{organizations.length}</p>
              <p className="text-sm text-slate-500">
                {language === 'es' ? 'Organizaciones' : 'Organizations'}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {organizations.reduce((acc, org) => acc + (org.user_count || 0), 0)}
              </p>
              <p className="text-sm text-slate-500">
                {language === 'es' ? 'Usuarios Totales' : 'Total Users'}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{plans.length}</p>
              <p className="text-sm text-slate-500">
                {language === 'es' ? 'Planes Disponibles' : 'Available Plans'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Organizations Table */}
      <Card>
        <CardHeader>
          <CardTitle>{language === 'es' ? 'Organizaciones' : 'Organizations'}</CardTitle>
          <CardDescription>
            {language === 'es' 
              ? 'Lista de todas las empresas registradas'
              : 'List of all registered companies'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{language === 'es' ? 'Nombre' : 'Name'}</TableHead>
                <TableHead>{language === 'es' ? 'Subdominio' : 'Subdomain'}</TableHead>
                <TableHead>{language === 'es' ? 'Plan' : 'Plan'}</TableHead>
                <TableHead>{language === 'es' ? 'Usuarios' : 'Users'}</TableHead>
                <TableHead>{language === 'es' ? 'Facturación' : 'Billing'}</TableHead>
                <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
                <TableHead className="text-right">{language === 'es' ? 'Acciones' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      {org.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    {org.subdomain ? (
                      <code className="px-2 py-1 bg-slate-100 rounded text-sm">{org.subdomain}</code>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={getPlanBadgeColor(org.plan?.name)}>
                      {org.plan?.display_name || '-'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span>{org.user_count || 0}</span>
                      {org.plan?.max_users > 0 && (
                        <span className="text-slate-400">/ {org.plan.max_users}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={BILLING_STATUS_COLORS[org.subscription_status] || 'bg-slate-100 text-slate-700'}>
                      {BILLING_STATUS_LABELS[language]?.[org.subscription_status] || org.subscription_status || '-'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={org.is_active}
                        onCheckedChange={() => handleToggleOrgStatus(org)}
                        disabled={togglingOrg === org.id}
                        data-testid={`toggle-org-${org.id}`}
                      />
                      <span className={`text-sm ${org.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {org.is_active 
                          ? (language === 'es' ? 'Activa' : 'Active')
                          : (language === 'es' ? 'Inactiva' : 'Inactive')}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          const kioskUrl = `${window.location.origin}/kiosk/${org.subdomain || org.id}`;
                          navigator.clipboard.writeText(kioskUrl);
                          alert(language === 'es' 
                            ? `URL del kiosko copiada: ${kioskUrl}`
                            : `Kiosk URL copied: ${kioskUrl}`);
                        }}
                        title={language === 'es' ? 'Copiar URL del Kiosko' : 'Copy Kiosk URL'}
                      >
                        <Clock className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditOrg(org)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteOrg(org)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={org.user_count > 0}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {organizations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    {language === 'es' 
                      ? 'No hay organizaciones registradas'
                      : 'No organizations registered'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Plans Section */}
      <Card>
        <CardHeader>
          <CardTitle>{language === 'es' ? 'Planes de Suscripción' : 'Subscription Plans'}</CardTitle>
          <CardDescription>
            {language === 'es' 
              ? 'Planes disponibles para las organizaciones'
              : 'Available plans for organizations'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <div 
                key={plan.id} 
                className={`p-4 rounded-lg border-2 ${
                  plan.name === 'enterprise' 
                    ? 'border-amber-200 bg-amber-50' 
                    : plan.name === 'professional'
                    ? 'border-indigo-200 bg-indigo-50'
                    : 'border-slate-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-1.5">
                    {plan.display_name}
                    {plan.is_active === false && (
                      <Badge className="bg-slate-200 text-slate-600 text-[10px]">
                        {language === 'es' ? 'Inactivo' : 'Inactive'}
                      </Badge>
                    )}
                  </h3>
                  <div className="flex items-center gap-1">
                    {plan.name === 'enterprise' && (
                      <Crown className="w-5 h-5 text-amber-500" />
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleEditPlan(plan)} data-testid={`edit-plan-${plan.name}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePlan(plan)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-plan-${plan.name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900 mb-2">
                  €{plan.price_monthly}
                  <span className="text-sm font-normal text-slate-500">/mes</span>
                  {plan.price_per_user > 0 && (
                    <span className="text-sm font-normal text-slate-500"> + €{plan.price_per_user}/{language === 'es' ? 'usuario' : 'user'}</span>
                  )}
                </p>
                <p className="text-sm text-slate-600 mb-3">
                  {plan.max_users === -1
                    ? (language === 'es' ? 'Usuarios ilimitados' : 'Unlimited users')
                    : `${plan.max_users} ${language === 'es' ? 'usuarios máx.' : 'users max.'}`}
                </p>
                {!plan.stripe_price_id && !plan.stripe_price_id_per_user && (
                  <p className="text-xs text-amber-600 mb-2">
                    {language === 'es' ? 'Sin Stripe Price ID configurado' : 'No Stripe Price ID configured'}
                  </p>
                )}
                <div className="text-xs text-slate-500">
                  {plan.features?.slice(0, 3).map((f, i) => (
                    <span key={i} className="inline-block mr-2">• {f}</span>
                  ))}
                  {plan.features?.length > 3 && (
                    <span className="text-indigo-600">+{plan.features.length - 3} más</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Organization Dialog */}
      <Dialog open={showOrgDialog} onOpenChange={setShowOrgDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingOrg 
                ? (language === 'es' ? 'Editar Organización' : 'Edit Organization')
                : (language === 'es' ? 'Nueva Organización' : 'New Organization')}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Completa los datos de la organización'
                : 'Fill in the organization details'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Nombre' : 'Name'}</Label>
              <Input
                value={orgForm.name}
                onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                placeholder={language === 'es' ? 'Nombre de la empresa' : 'Company name'}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Subdominio (opcional)' : 'Subdomain (optional)'}</Label>
              <Input
                value={orgForm.subdomain}
                onChange={(e) => setOrgForm({ ...orgForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="mi-empresa"
              />
              <p className="text-xs text-slate-500">
                {language === 'es' 
                  ? 'Se usará para acceso personalizado y el Kiosko. Se genera automáticamente si no se indica.'
                  : 'Will be used for custom access and Kiosk. Auto-generated if not provided.'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Plan' : 'Plan'}</Label>
              <Select
                value={orgForm.plan_id}
                onValueChange={(value) => setOrgForm({ ...orgForm, plan_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={language === 'es' ? 'Seleccionar plan' : 'Select plan'} />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.display_name} - €{plan.price_monthly}/mes
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Admin section - only for new organizations */}
            {!editingOrg && (
              <div className="pt-4 border-t border-slate-200">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  {language === 'es' ? 'Administrador de la Organización' : 'Organization Administrator'}
                  <span className="text-xs font-normal text-slate-500">
                    ({language === 'es' ? 'opcional' : 'optional'})
                  </span>
                </h4>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Nombre del administrador' : 'Admin name'}</Label>
                    <Input
                      value={orgForm.admin_name}
                      onChange={(e) => setOrgForm({ ...orgForm, admin_name: e.target.value })}
                      placeholder={language === 'es' ? 'Nombre completo' : 'Full name'}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Email del administrador' : 'Admin email'}</Label>
                    <Input
                      type="email"
                      value={orgForm.admin_email}
                      onChange={(e) => setOrgForm({ ...orgForm, admin_email: e.target.value })}
                      placeholder="admin@empresa.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Contraseña' : 'Password'}</Label>
                    <Input
                      type="password"
                      value={orgForm.admin_password}
                      onChange={(e) => setOrgForm({ ...orgForm, admin_password: e.target.value })}
                      placeholder={language === 'es' ? 'Contraseña del administrador' : 'Admin password'}
                    />
                  </div>
                  
                  <p className="text-xs text-slate-500">
                    {language === 'es' 
                      ? 'Si completas estos campos, se creará automáticamente un usuario administrador para esta organización.'
                      : 'If you fill these fields, an admin user will be automatically created for this organization.'}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrgDialog(false)}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSaveOrg} 
              disabled={!orgForm.name || !orgForm.plan_id || creatingOrg}
            >
              {creatingOrg ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {language === 'es' ? 'Creando...' : 'Creating...'}
                </span>
              ) : (
                language === 'es' ? 'Guardar' : 'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPlan
                ? (language === 'es' ? 'Editar Plan' : 'Edit Plan')
                : (language === 'es' ? 'Nuevo Plan' : 'New Plan')}
            </DialogTitle>
            <DialogDescription>
              {language === 'es'
                ? 'Configura el plan de suscripción'
                : 'Configure the subscription plan'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!editingPlan && (
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Identificador' : 'Identifier'}</Label>
                <Input
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  placeholder="starter"
                />
                <p className="text-xs text-slate-500">
                  {language === 'es'
                    ? 'Identificador interno único (sin espacios). No se puede cambiar después.'
                    : 'Unique internal identifier (no spaces). Cannot be changed later.'}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{language === 'es' ? 'Nombre visible' : 'Display name'}</Label>
              <Input
                value={planForm.display_name}
                onChange={(e) => setPlanForm({ ...planForm, display_name: e.target.value })}
                placeholder={language === 'es' ? 'Plan Starter' : 'Starter Plan'}
              />
            </div>

            <p className="text-xs text-slate-500 -mt-2">
              {language === 'es'
                ? 'Combina un precio fijo con uno por usuario, o deja el fijo en 0 para cobrar solo por usuario.'
                : 'Combine a fixed price with a per-user price, or leave the fixed one at 0 to bill only per user.'}
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Precio fijo mensual (€)' : 'Fixed monthly price (€)'}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={planForm.price_monthly}
                  onChange={(e) => setPlanForm({ ...planForm, price_monthly: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Usuarios máx. (-1 = ilimitado)' : 'Max users (-1 = unlimited)'}</Label>
                <Input
                  type="number"
                  value={planForm.max_users}
                  onChange={(e) => setPlanForm({ ...planForm, max_users: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Stripe Price ID {language === 'es' ? '(componente fijo)' : '(fixed component)'}</Label>
              <Input
                value={planForm.stripe_price_id}
                onChange={(e) => setPlanForm({ ...planForm, stripe_price_id: e.target.value })}
                placeholder="price_1AbCdEfGhIjKlMn"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Precio por usuario/mes (€)' : 'Price per user/month (€)'}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={planForm.price_per_user}
                  onChange={(e) => setPlanForm({ ...planForm, price_per_user: e.target.value })}
                  placeholder={language === 'es' ? 'Opcional' : 'Optional'}
                />
              </div>
              <div className="space-y-2">
                <Label>Stripe Price ID {language === 'es' ? '(por usuario)' : '(per user)'}</Label>
                <Input
                  value={planForm.stripe_price_id_per_user}
                  onChange={(e) => setPlanForm({ ...planForm, stripe_price_id_per_user: e.target.value })}
                  placeholder="price_1XyZ..."
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              {language === 'es'
                ? 'El Stripe Price ID por usuario debe ser un precio recurrente "por unidad de cantidad" (licensed). La cantidad se sincroniza automáticamente con el nº de usuarios activos.'
                : 'The per-user Stripe Price ID must be a recurring, per-unit ("licensed") price. Quantity is synced automatically with the number of active users.'}
            </p>

            <div className="space-y-2">
              <Label>{language === 'es' ? 'Funcionalidades (separadas por comas)' : 'Features (comma-separated)'}</Label>
              <Textarea
                value={planForm.features}
                onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
                placeholder="fichajes, turnos, vacaciones, kiosko"
                rows={3}
              />
            </div>

            {editingPlan && (
              <div className="flex items-center justify-between">
                <Label>{language === 'es' ? 'Plan activo' : 'Active plan'}</Label>
                <Switch
                  checked={planForm.is_active}
                  onCheckedChange={(checked) => setPlanForm({ ...planForm, is_active: checked })}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSavePlan}
              disabled={!planForm.display_name || (!editingPlan && !planForm.name) || savingPlan}
              data-testid="save-plan-btn"
            >
              {savingPlan ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {language === 'es' ? 'Guardando...' : 'Saving...'}
                </span>
              ) : (
                language === 'es' ? 'Guardar' : 'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Organizations;
